import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import axios from "axios";
import AIwrapperchat from "../../components/scene-ai/aiwrapperchat";

/**
 * AI Wrapper Page:
 * - Displays the page in a full-height, full-width iframe on the left.
 * - Places the AI chat sidebar (AIwrapperchat) on the right.
 */
const AIWrapperPage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // --- Read Query Parameters ---
    const urlParams = new URLSearchParams(window.location.search);
    const paramApiid = urlParams.get("apiid") || "";
    const paramUrl = urlParams.get("url") || "";

    // --- Local State ---
    const [pageData, setPageData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // --- Fetch Page Data ---
    useEffect(() => {
        const fetchPageData = async () => {
            if (!paramApiid) {
                setError("API ID is required to fetch AI data.");
                return;
            }
            setLoading(true);
            setError("");
            try {
                const response = await axios.get(
                    "https://api.swancreate.com/api/llm/sail_ai_pages",
                    { params: { apiid: paramApiid } }
                );
                // If the response is an array, take the first element
                const data = Array.isArray(response.data)
                    ? response.data[0]
                    : response.data;
                setPageData(data);
            } catch (err) {
                console.error("Error fetching AI data:", err);
                setError("Unable to load AI page data.");
            } finally {
                setLoading(false);
            }
        };

        fetchPageData();
    }, [paramApiid]);

    // --- Determine the Iframe URL ---
    // Use the query parameter URL if provided; otherwise, use the PageUrl from the API data.
    const iframeUrl = paramUrl || pageData?.PageUrl || "";

    return (
        <Box
            sx={{
                display: "flex",
                height: "100vh",
                width: "100vw",
                backgroundColor: colors.primary[900],
                overflow: "hidden",
            }}
        >
            {/* Left Side: Fully Expanded Iframe */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                }}
            >
                {loading ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography
                        sx={{
                            margin: "auto",
                            textAlign: "center",
                            color: "#ffcccc",
                        }}
                    >
                        {error}
                    </Typography>
                ) : (
                    <iframe
                        src={iframeUrl}
                        style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            flex: 1,
                        }}
                        title={pageData?.PageTitle || "AI Iframe"}
                    />
                )}
            </Box>

            {/* Right Side: AI Chat Sidebar */}
            {/* Pass the API ID so that AIwrapperchat can fetch its own data */}
            <AIwrapperchat apiid={paramApiid} />
        </Box>
    );
};

export default AIWrapperPage;
